import React, { useEffect, useState } from 'react';
import {
  Box,
  FormControl,
  MenuItem,
  Select as MuiSelect,
} from '@mui/material';
import { MdEdit } from 'react-icons/md';
import { confirmWrapper } from 'src/components/Dialog';
import {
  ExpandUpIcon,
  CloseActiveIcon,
  CloseSquareIcon,
  ConfirmActiveIcon,
  ConfirmSquareIcon,
} from 'src/icons';
import { Option } from '../../types';

const getValueFromOptions = (options: Option[], value: string) => {
  if (options) {
    return options.find((option) => option.value === value);
  }
  return undefined;
};

const InlineSelect = function (props: any) {
  const {
    value,
    label,
    options = [],
    confirmFunction,
    labelEdit = true,
    inlineLabel = '',
    isEditable = true,
  } = props;

  const [tempValue, setTempValue] = useState(undefined);
  const [newValue, setNewValue] = useState(value);
  const [editMode, togleEditMode] = useState(false);

  const [isCloseHovered, setIsCloseHovered] = useState(false);
  const [isConfirmHovered, setIsConfirmHovered] = useState(false);

  const handleMouseEnterClose = () => {
    setIsCloseHovered(true);
  };

  const handleMouseLeaveClose = () => {
    setIsCloseHovered(false);
  };

  const handleMouseEnterConfirm = () => {
    setIsConfirmHovered(true);
  };

  const handleMouseLeaveConfirm = () => {
    setIsConfirmHovered(false);
  };

  useEffect(() => {
    setNewValue(value);
    setTempValue(undefined);
    togleEditMode(false);
  }, [value]);

  const handleChange = (e: any) => {
    setTempValue(e.target.value);
  };

  const onEdit = () => {
    setTempValue(newValue);
    togleEditMode((prevState) => !prevState);
  };

  const onCancel = () => {
    setTempValue(undefined);
    togleEditMode(false);
  };

  const onClickUpdate = async () => {
    const confirmation = await confirmWrapper(
      `Are you sure you want to update ${label || inlineLabel}?`,
      {
        variant: 'warning',
        primaryBtnLabel: 'Proceed',
        secondaryBtnLabel: 'Cancel',
      },
    );

    if (confirmation) {
      try {
        await confirmFunction(tempValue);
        setNewValue(tempValue);
        togleEditMode(false);
      } catch (e) {
        console.log(e);
      }
    }
  };

  return (
    <Box className="w-full h-full group">
      {!inlineLabel ? (
        <>
          {label && (
            <Box className="flex gap-2">
              <Box className="text-sm leading-5 text-[#828A97] pb-1">
                {label}
              </Box>
              {isEditable && !editMode && labelEdit && (
                <Box
                  className="cursor-pointer pb-1 group-hover:visible invisible"
                  onClick={onEdit}
                >
                  <MdEdit size="14" color="#828A97" />
                </Box>
              )}
            </Box>
          )}
          {
            editMode ? (
              <Box className="relative w-full h-full">
                <FormControl sx={{ width: '100%' }}>
                  <MuiSelect
                    placeholder={label}
                    fullWidth
                    value={tempValue}
                    onChange={handleChange}
                    variant="outlined"
                    onClick={(event: any) => {
                      event.stopPropagation();
                    }}
                    sx={{
                      '& .MuiOutlinedInput-input': {
                        paddingLeft: '2px!important',
                        paddingRight: '0px!important',
                        paddingTop: '2px!important',
                        paddingBottom: '2px!important',
                      },
                    }}
                    MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
                    renderValue={(selectedValue) => {
                      const selectedOption = options.find(
                        (option: any) => option.value === selectedValue,
                      );
                      const labelValue = selectedOption ? selectedOption.label : '';
                      if (React.isValidElement(labelValue)) {
                        return labelValue;
                      }
                      return (
                        <span className="text-[14px] font-[500]">
                          {labelValue as string}
                        </span>
                      );
                    }}
                    IconComponent={ExpandUpIcon}
                  >
                    {options.map((option: any) => (
                      <MenuItem value={option.value as any}>
                        {option.label as any}
                      </MenuItem>
                    ))}
                  </MuiSelect>
                </FormControl>
                <Box className="absolute flex gap-[2px] right-0">
                  <Box
                    className="cursor-pointer"
                    onMouseEnter={handleMouseEnterClose}
                    onMouseLeave={handleMouseLeaveClose}
                    onClick={onCancel}
                  >
                    {isCloseHovered ? (
                      <CloseActiveIcon size={20} />
                    ) : (
                      <CloseSquareIcon size={20} />
                    )}
                  </Box>
                  <Box
                    className="cursor-pointer"
                    onMouseEnter={handleMouseEnterConfirm}
                    onMouseLeave={handleMouseLeaveConfirm}
                    onClick={onClickUpdate}
                  >
                    {isConfirmHovered ? (
                      <ConfirmActiveIcon size={20} />
                    ) : (
                      <ConfirmSquareIcon size={20} />
                    )}
                  </Box>
                </Box>
              </Box>
            ) : (
              <Box className="flex">
                <Box className="text-base overflow-hidden text-ellipsis">
                  {getValueFromOptions(options, newValue)?.label}
                </Box>
              </Box>
            )
          }
        </>
      ) : (
        <Box className="w-full h-full">
          <Box className="flex items-center gap-3">
            <Box className="grow-0 shrink-0 text-sm leading-5 text-[#828A97]">
              {inlineLabel}
              :
            </Box>
            {!editMode ? (
              <>
                <Box className="text-base overflow-hidden text-ellipsis">
                  {getValueFromOptions(options, newValue)?.label}
                </Box>

                {isEditable && (
                  <Box
                    className="cursor-pointer group-hover:visible invisible"
                    onClick={onEdit}
                  >
                    <MdEdit size="14" color="#828A97" />
                  </Box>
                )}
              </>
            ) : (
              <Box className="relative w-full h-full">
                <FormControl sx={{ width: '100%' }}>
                  <MuiSelect
                    placeholder={label}
                    fullWidth
                    value={tempValue}
                    onChange={handleChange}
                    variant="outlined"
                    onClick={(event: any) => {
                      event.stopPropagation();
                    }}
                    sx={{
                      '& .MuiOutlinedInput-input': {
                        paddingLeft: '2px!important',
                        paddingRight: '0px!important',
                        paddingTop: '2px!important',
                        paddingBottom: '2px!important',
                      },
                    }}
                    MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
                    renderValue={(selectedValue) => {
                      const selectedOption = options.find(
                        (option: any) => option.value === selectedValue,
                      );
                      const labelValue = selectedOption ? selectedOption.label : '';
                      if (React.isValidElement(labelValue)) {
                        return labelValue;
                      }
                      return (
                        <span className="text-[14px] font-[500]">
                          {labelValue as string}
                        </span>
                      );
                    }}
                    IconComponent={ExpandUpIcon}
                  >
                    {options.map((option: any) => (
                      <MenuItem value={option.value as any}>
                        {option.label as any}
                      </MenuItem>
                    ))}
                  </MuiSelect>
                </FormControl>
                <Box className="absolute flex gap-[2px] right-0">
                  <Box
                    className="cursor-pointer"
                    onMouseEnter={handleMouseEnterClose}
                    onMouseLeave={handleMouseLeaveClose}
                    onClick={onCancel}
                  >
                    {isCloseHovered ? (
                      <CloseActiveIcon size={20} />
                    ) : (
                      <CloseSquareIcon size={20} />
                    )}
                  </Box>
                  <Box
                    className="cursor-pointer"
                    onMouseEnter={handleMouseEnterConfirm}
                    onMouseLeave={handleMouseLeaveConfirm}
                    onClick={onClickUpdate}
                  >
                    {isConfirmHovered ? (
                      <ConfirmActiveIcon size={20} />
                    ) : (
                      <ConfirmSquareIcon size={20} />
                    )}
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default InlineSelect;
